import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, Loader2, MessageSquare, Package, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { SeleccionarTodosCheckbox } from "@/components/SeleccionarTodosCheckbox";
import { descargarRespuestaBlob } from "@/lib/download";
import { BotonAccion, ModalComentario, ModalNuevaVersion, ModalEliminar } from "@/components/AccionesDocumento";
import { ListaComentarios, type ObservacionResumen } from "@/components/ComentariosDocumento";

/**
 * Documento listado en el detalle de una solicitud. Es la forma que devuelve
 * {@code GET /api/documentos/contexto}, común a todos los sectores.
 */
export interface DocumentoListado {
  documentoId: string;
  versionVigenteId: string;
  tipoNombre: string;
  nombreArchivo: string;
  sizeBytes: number;
  fechaCarga: string;
  antivirusEstado?: string;
}

/**
 * Lista de documentos con selección múltiple y descarga en ZIP.
 *
 * Vive en `components/` y no en la página de un sector porque la selección y el
 * empaquetado no dependen del sector: operan sobre versiones de documento, que
 * es la pieza común. Cualquier pantalla que liste documentos puede usarla.
 *
 * La descarga individual sigue disponible en cada renglón: para un solo archivo
 * abrir un ZIP estorba.
 */
export function DocumentosSeleccionables({
  docs,
  motivoDescarga,
  renderMeta,
  renderAccion,
  onCambio,
  contextoTipo,
  contextoId,
  tramiteId,
  renderAccionExtra,
  permitirEscritura = true,
}: {
  docs: DocumentoListado[];
  /** Queda en bitácora, junto al evento de descarga de cada documento. */
  motivoDescarga: string;
  /** Metadatos extra del renglón (insignia de antivirus, etiquetas del sector…). */
  renderMeta?: (d: DocumentoListado) => React.ReactNode;
  /**
   * Acción individual del renglón. Por omisión, descarga directa. Telecom pasa
   * la suya («Ver / descargar», que abre la vista previa) para no cambiarle la
   * forma de trabajar a nadie: el requerimiento pide añadir la descarga
   * múltiple, no sustituir la que ya existe.
   */
  renderAccion?: (d: DocumentoListado) => React.ReactNode;
  /** Se llama tras comentar, versionar o retirar, para releer la lista. */
  onCambio?: () => void | Promise<void>;
  /**
   * Contexto del expediente (el mismo con el que se listaron los documentos).
   * Con él, cada renglón muestra sus comentarios; sin él, el bloque de
   * comentarios no aparece —la lista sigue sirviendo para descargar—.
   */
  contextoTipo?: string;
  contextoId?: string;
  /**
   * Alternativa a `contextoTipo`/`contextoId` cuando la lista abarca el trámite
   * completo. Hace falta porque cada sector cuelga sus documentos de un contexto
   * distinto —Telecom del expediente, Seguridad de la solicitud— y los
   * comentarios de la solicitud no caben en un solo par contexto/id.
   */
  tramiteId?: string;
  /** Botón propio del sector añadido antes de la descarga, sin sustituirla. */
  renderAccionExtra?: (d: DocumentoListado) => React.ReactNode;
  /**
   * Si es `false`, la lista queda de consulta: se ve, se descarga y se comenta,
   * pero no ofrece «Nueva versión» ni «Eliminar». Telecom lista aquí documentos
   * que se administran desde su expediente, con sus reglas de vinculación y
   * completitud; duplicar ahí esas acciones las dejaría fuera de esas reglas.
   */
  permitirEscritura?: boolean;
}) {
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [descargandoUno, setDescargandoUno] = useState<string | null>(null);
  const [empaquetando, setEmpaquetando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<{ tipo: "comentar" | "version" | "eliminar"; doc: DocumentoListado } | null>(null);
  const [comentariosAbiertos, setComentariosAbiertos] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  // Comentarios de TODO el expediente en una sola petición: así cada renglón
  // puede decir cuántos tiene sin disparar una consulta por documento. Antes,
  // desde esta lista se podía comentar pero no leer lo comentado, y el
  // comentario guardado parecía perdido.
  const urlComentarios = tramiteId
    ? `/api/documentos/tramite/${tramiteId}/observaciones`
    : `/api/documentos/contexto/observaciones?contextoTipo=${contextoTipo}&contextoId=${contextoId}`;
  const qComentarios = useQuery<ObservacionResumen[]>({
    queryKey: tramiteId
      ? ["doc-observaciones", "tramite", tramiteId]
      : ["doc-observaciones", "contexto", contextoTipo, contextoId],
    enabled: !!tramiteId || (!!contextoTipo && !!contextoId),
    queryFn: async () => (await api.get<ObservacionResumen[]>(urlComentarios)).data,
  });
  const hayComentarios = !!tramiteId || (!!contextoTipo && !!contextoId);

  const comentariosDe = (documentoId: string) =>
    (qComentarios.data ?? []).filter((o) => o.documentoId === documentoId);

  const alternarComentarios = (documentoId: string) =>
    setComentariosAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(documentoId)) s.delete(documentoId); else s.add(documentoId);
      return s;
    });

  // Las acciones dependen del trámite —su estado y su empresa—, que es el mismo
  // para todos los documentos de la lista. Se pregunta una vez con el primero
  // en vez de una petición por renglón.
  const qAcciones = useQuery<AccionesDocumento>({
    queryKey: ["doc-acciones", docs[0]?.documentoId],
    enabled: docs.length > 0,
    queryFn: async () =>
      (await api.get<AccionesDocumento>(`/api/documentos/${docs[0].documentoId}/acciones`)).data,
  });
  const acciones = qAcciones.data;

  /** Releer lo que pudo moverse tras comentar, versionar o retirar. */
  const trasCambiar = async () => {
    await qc.invalidateQueries({ queryKey: ["doc-acciones"] });
    await qc.invalidateQueries({ queryKey: ["doc-observaciones"] });
    await onCambio?.();
  };

  /** Para los diálogos que sí se cierran solos al terminar (versión y retiro). */
  const trasCambiarYCerrar = async () => {
    setAccion(null);
    await trasCambiar();
  };

  const ids = docs.map((d) => d.versionVigenteId);
  const seleccionados = ids.filter((id) => marcados.has(id));

  const toggle = (id: string) =>
    setMarcados((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });

  const toggleTodos = (todosIds: string[], marcar: boolean) =>
    setMarcados((prev) => {
      const s = new Set(prev);
      todosIds.forEach((id) => (marcar ? s.add(id) : s.delete(id)));
      return s;
    });

  const descargarUno = async (versionId: string) => {
    setDescargandoUno(versionId);
    setError(null);
    try {
      const { data } = await api.post<{ url: string }>(
        `/api/documentos/versiones/${versionId}/descarga`,
        { motivo: motivoDescarga },
      );
      window.open(data.url, "_blank", "noopener");
    } catch {
      setError("No se pudo descargar el documento.");
    } finally {
      setDescargandoUno(null);
    }
  };

  const descargarSeleccionados = async () => {
    if (seleccionados.length === 0) return;
    // Un solo archivo no necesita ZIP: se descarga como siempre.
    if (seleccionados.length === 1) {
      await descargarUno(seleccionados[0]);
      return;
    }
    setEmpaquetando(true);
    setError(null);
    try {
      const resp = await api.post(
        "/api/documentos/descarga-multiple",
        { versionIds: seleccionados, motivo: motivoDescarga },
        { responseType: "blob" },
      );
      descargarRespuestaBlob(resp, `documentos-${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "No se pudo preparar el paquete de descarga.");
    } finally {
      setEmpaquetando(false);
    }
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b pb-2">
        <SeleccionarTodosCheckbox
          ids={ids}
          seleccionados={marcados}
          onToggleTodos={toggleTodos}
          className="text-[11px]"
        />
        <button
          type="button"
          onClick={descargarSeleccionados}
          disabled={seleccionados.length === 0 || empaquetando}
          className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint disabled:cursor-not-allowed disabled:opacity-40"
        >
          {empaquetando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
          {empaquetando
            ? "Preparando…"
            : `Descargar seleccionados${seleccionados.length > 0 ? ` (${seleccionados.length})` : ""}`}
        </button>
      </div>

      {error && (
        <div className="mb-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">{error}</div>
      )}

      <ul className="divide-y">
        {docs.map((d) => (
          <li key={d.documentoId} className="flex flex-col gap-1 py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={marcados.has(d.versionVigenteId)}
                onChange={() => toggle(d.versionVigenteId)}
                aria-label={`Seleccionar ${d.nombreArchivo}`}
                className="h-4 w-4 shrink-0 cursor-pointer"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-fg" title={d.nombreArchivo}>
                  {d.nombreArchivo}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
                  <span>{d.tipoNombre}</span>
                  <span>· {formatBytes(d.sizeBytes)}</span>
                  {renderMeta?.(d)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {acciones?.puedeComentar && (
                  <BotonAccion icon={<MessageSquare className="h-3.5 w-3.5" />}
                               onClick={() => setAccion({ tipo: "comentar", doc: d })}>
                    Comentar
                  </BotonAccion>
                )}
                {permitirEscritura && acciones?.puedeNuevaVersion && (
                  <BotonAccion icon={<Upload className="h-3.5 w-3.5" />}
                               onClick={() => setAccion({ tipo: "version", doc: d })}>
                    Nueva versión
                  </BotonAccion>
                )}
                {permitirEscritura && acciones?.puedeEliminar && (
                  <BotonAccion peligro icon={<Trash2 className="h-3.5 w-3.5" />}
                               onClick={() => setAccion({ tipo: "eliminar", doc: d })}>
                    Eliminar
                  </BotonAccion>
                )}
              </div>
              {renderAccionExtra && <div className="shrink-0">{renderAccionExtra(d)}</div>}
              {renderAccion ? (
                <div className="shrink-0">{renderAccion(d)}</div>
              ) : (
                <button
                  type="button"
                  onClick={() => descargarUno(d.versionVigenteId)}
                  disabled={descargandoUno === d.versionVigenteId}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint disabled:opacity-50"
                >
                  {descargandoUno === d.versionVigenteId ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  Descargar
                </button>
              )}
            </div>

            {/* Comentarios del documento: visibles aquí mismo, sin abrir el visor. */}
            {hayComentarios && comentariosDe(d.documentoId).length > 0 && (
              <div className="w-full">
                <button
                  type="button"
                  onClick={() => alternarComentarios(d.documentoId)}
                  aria-expanded={comentariosAbiertos.has(d.documentoId)}
                  className="inline-flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-xs text-primary-strong transition hover:bg-primary-tint"
                >
                  {comentariosAbiertos.has(d.documentoId)
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronRight className="h-3.5 w-3.5" />}
                  <MessageSquare className="h-3.5 w-3.5" />
                  {comentariosDe(d.documentoId).length === 1
                    ? "1 comentario"
                    : `${comentariosDe(d.documentoId).length} comentarios`}
                </button>
                {comentariosAbiertos.has(d.documentoId) && (
                  <div className="mt-1 rounded border bg-surface-2">
                    <ListaComentarios observaciones={comentariosDe(d.documentoId)} />
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {accion?.tipo === "comentar" && (
        <ModalComentario versionId={accion.doc.versionVigenteId}
                         onClose={() => {
                           // Al cerrar, deja abierto el hilo del documento: el
                           // comentario recién guardado se ve sin buscarlo.
                           const documentoId = accion.doc.documentoId;
                           setAccion(null);
                           setComentariosAbiertos((prev) => new Set(prev).add(documentoId));
                         }}
                         onHecho={trasCambiar} />
      )}
      {accion?.tipo === "version" && (
        <ModalNuevaVersion documentoId={accion.doc.documentoId}
                           onClose={() => setAccion(null)} onHecho={trasCambiarYCerrar} />
      )}
      {accion?.tipo === "eliminar" && (
        <ModalEliminar documentoId={accion.doc.documentoId}
                       nombreArchivo={accion.doc.nombreArchivo}
                       esBorrador={acciones?.esBorrador ?? false}
                       onClose={() => setAccion(null)} onHecho={trasCambiarYCerrar} />
      )}
    </div>
  );
}

interface AccionesDocumento {
  puedeDescargar: boolean;
  puedeNuevaVersion: boolean;
  puedeComentar: boolean;
  puedeEliminar: boolean;
  esBorrador: boolean;
  tramiteCerrado: boolean;
  estadoTramite: string;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
